import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { openProfileDrawer } from './profileDrawerHelper';
import { S3Image, getAvatarStyle } from '../../../lib/s3';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

const GET_COMPANY_SETTINGS = gql`
  query GetCompanySettings {
    company {
      id
      agent_word
      agent_word_fr
      avatar_background_color
      avatar_directory
      primary_color
    }
  }
`;

const DealNdaOutForReview = ({ deal, refetchDeal }) => {
  const intl = useIntl();
  const { data: settingsData } = useQuery(GET_COMPANY_SETTINGS, {
    fetchPolicy: 'cache-first'
  });
  const companyData = settingsData?.company || {};
  const companySettings = {
    avatar_directory: companyData.avatar_directory,
    avatar_background_color: companyData.avatar_background_color,
    primary_color: companyData.primary_color,
  };
  const agentWord = (intl.locale === 'fr' ? (companyData.agent_word_fr || 'Conseiller(ère)') : (companyData.agent_word || 'advisor')).toLowerCase();
  const agentWordLabel = agentWord.charAt(0).toUpperCase() + agentWord.slice(1);
  const agentWordPlural = intl.locale === 'fr' && !companyData.agent_word_fr
    ? 'conseillers(ères)'
    : (agentWord.endsWith('s') ? agentWord : `${agentWord}s`);
  const agentWordPluralLower = agentWordPlural.toLowerCase();

  // Filter deal interests to show NDAs out for review
  const ndaOutForReviewInterests = deal?.nda_out_for_review?.filter(
    interest => 
      interest.workflow_state === 'out_for_receiving_signature' 
  ) || [];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          <FormattedMessage
            id="dealNda.outForReview.title"
            defaultMessage="NDAs Out for Review"
          />
        </h2>
        <p className="text-gray-600">
          {ndaOutForReviewInterests.length === 0 
            ? (
              <FormattedMessage
                id="dealNda.outForReview.noNdas"
                defaultMessage="No NDAs out for review."
              />
            )
            : <FormattedMessage 
                id="dealNda.outForReview.description" 
                defaultMessage="You have {count} NDA{count, plural, one {} other {s}} out for review."
                values={{ count: ndaOutForReviewInterests.length }}
              />
          }
        </p>
      </div>
      
      {ndaOutForReviewInterests.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {agentWordLabel}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{intl.formatMessage({ id: 'dealNda.outForReview.column.location' })}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{intl.formatMessage({ id: 'dealNda.outForReview.column.date_sent' })}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{intl.formatMessage({ id: 'dealNda.outForReview.column.status' })}</th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">View</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ndaOutForReviewInterests.map(interest => {
                const advisor = interest?.inventory_user?.user;
                if (!advisor) return null;
                const avatarStyle = getAvatarStyle(advisor.profile_picture, companySettings);
                
                return (
                  <tr key={interest.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className="h-10 w-10 rounded-full overflow-hidden flex items-center justify-center text-gray-500 text-sm font-semibold"
                          style={{ backgroundColor: avatarStyle.backgroundColor }}
                        >
                          <img src={avatarStyle.url} alt="" className="w-10 h-10 rounded-full object-cover" style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}} />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{advisor?.allowed_name || 'Unknown'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {advisor?.city ? `${advisor.city}${advisor.province ? `, ${advisor.province}` : ''}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(() => {
                        if (!interest.receiving_party_nda_sent_timestamp) return intl.formatMessage({ id: 'dealNda.outForReview.na' });

                        const date = new Date(interest.receiving_party_nda_sent_timestamp);
                        if (isNaN(date.getTime())) return intl.formatMessage({ id: 'dealNda.outForReview.na' });

                        return intl.formatDate(date, { year: 'numeric', month: 'short', day: 'numeric' });
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {intl.formatMessage({ id: 'dealNda.outForReview.status.awaiting_signature' })}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          if (advisor?.fb_user_name) {
                            openProfileDrawer(advisor.fb_user_name, { fromDeal: true, dealInterest: interest });
                          }
                        }}
                        className="text-blue-600 hover:text-blue-900 bg-transparent border-0 cursor-pointer"
                      >
                        {intl.formatMessage({ id: 'dealNda.outForReview.action.view_details' })}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-10 text-center">
          <div className="flex flex-col items-center justify-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {intl.formatMessage({
                id: 'dealNda.outForReview.empty_state.title',
                defaultMessage: 'No NDAs Out for Review'
              })}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
                {intl.formatMessage(
                  {
                    id: 'dealNda.outForReview.empty_state.description_with_agent_word',
                    defaultMessage: 'NDAs awaiting {agentWordPlural} signatures will appear here.'
                  },
                  { agentWordPlural: agentWordPluralLower }
                )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

DealNdaOutForReview.propTypes = {
  deal: PropTypes.object.isRequired,
  refetchDeal: PropTypes.func.isRequired
};

export default DealNdaOutForReview; 